import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Heart,
    Brain,
    Award,
    Users,
    Target,
    CheckCircle,
    ArrowRight,
    Sparkles,
    Shield,
    TrendingUp,
    Zap,
    Lightbulb,
    UserCheck,
    Smartphone,
    Quote
} from 'lucide-react';

const AboutPage = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative pt-32 pb-32 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 overflow-hidden">
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium text-white mb-8">
                            <Sparkles className="text-indigo-400" size={18} />
                            <span>Platformă fondată de psihologi</span>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                            Despre{' '}
                            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
                                Affinity Club
                            </span>
                        </h1>

                        <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed max-w-3xl mx-auto font-light">
                            O experiență autentică de autocunoaștere prin teste psihologice validate clinic,
                            create pentru a te ajuta să te înțelegi cu adevărat.
                        </p>

                        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 max-w-3xl mx-auto">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                                    <Brain className="text-white" size={24} />
                                </div>
                                <p className="text-lg text-slate-200 leading-relaxed text-left">
                                    <strong className="text-white">Nu chestionare superficiale</strong>,
                                    ci o înțelegere autentică a sinelui, bazată pe{' '}
                                    <strong className="text-white">știință și psihologie modernă</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-6 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { number: "4", label: "Niveluri de evaluare", color: "text-indigo-600" },
                            { number: "16", label: "Dimensiuni de personalitate", color: "text-purple-600" },
                            { number: "100%", label: "Confidențial", color: "text-emerald-600" },
                            { number: "AI", label: "Analiză personalizată", color: "text-rose-600" }
                        ].map((stat, idx) => (
                            <div key={idx} className="text-center">
                                <div className={`text-4xl md:text-5xl font-bold ${stat.color} mb-2`}>{stat.number}</div>
                                <div className="text-slate-600 text-sm font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Echipa de Psihologi */}
            <section id="echipa" className="py-28 px-6 bg-gradient-to-b from-white to-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-6">
                            <Users size={18} />
                            Echipa noastră
                        </div>
                        <h2 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
                            Psihologii din spatele{' '}
                            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                Affinity Club
                            </span>
                        </h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto font-light">
                            Profesioniști cu experiență clinică și formare complexă în psihoterapie și dezvoltare personală
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">
                        {/* Adela Silaghi */}
                        <article className="group bg-white rounded-3xl p-8 border border-slate-200 shadow-lg hover:shadow-2xl hover:border-indigo-200 transition-all duration-500">
                            <div className="flex flex-col items-center text-center">
                                <div className="relative mb-6">
                                    <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-white shadow-xl ring-4 ring-indigo-100 group-hover:ring-indigo-200 transition-all duration-500">
                                        <img
                                            src="https://affinityclub.agency/wp-content/uploads/2025/12/Adela-Silaghi.jpg"
                                            alt="Adela Silaghi"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg">
                                        Psihoterapeut
                                    </div>
                                </div>

                                <h3 className="text-2xl font-bold text-slate-900 mb-1">Adela Silaghi</h3>
                                <p className="text-indigo-600 font-medium mb-6">Psihoterapeut integrativ</p>

                                <div className="w-full space-y-3">
                                    {[
                                        "Master Psihologie Clinică & Intervenție",
                                        "Formare în psihoterapie corporală",
                                        "Specializare în hipnoză",
                                        "Terapii expresiv-creative"
                                    ].map((item, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <CheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" size={18} />
                                            <span className="text-slate-600 text-sm text-left">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </article>

                        {/* Roxana Carmen Cîrstea */}
                        <article className="group bg-white rounded-3xl p-8 border border-slate-200 shadow-lg hover:shadow-2xl hover:border-emerald-200 transition-all duration-500">
                            <div className="flex flex-col items-center text-center">
                                <div className="relative mb-6">
                                    <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-white shadow-xl ring-4 ring-emerald-100 group-hover:ring-emerald-200 transition-all duration-500">
                                        <img
                                            src="https://affinityclub.agency/wp-content/uploads/2025/12/Roxana-Carmen-Cirstea.jpg"
                                            alt="Roxana Carmen Cîrstea"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg">
                                        Psihoterapeut
                                    </div>
                                </div>

                                <h3 className="text-2xl font-bold text-slate-900 mb-1">Roxana Carmen Cîrstea</h3>
                                <p className="text-emerald-600 font-medium mb-6">Psihoterapeut transpersonal</p>

                                <div className="w-full space-y-3">
                                    {[
                                        "Master Psihologie",
                                        "Formare în coaching profesional",
                                        "Certificare în Qigong",
                                        "Certificare COPSI Pneuma Breathwork"
                                    ].map((item, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <CheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" size={18} />
                                            <span className="text-slate-600 text-sm text-left">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            {/* Misiunea noastră */}
            <section id="misiune" className="py-28 px-6 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-14">
                            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-semibold mb-6">
                                <Target size={18} />
                                Misiunea noastră
                            </div>
                            <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight">
                                De ce există{' '}
                                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                                    Affinity Club
                                </span>
                            </h2>
                        </div>

                        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10">
                            <div className="space-y-6">
                                <p className="text-lg text-slate-200 leading-relaxed">
                                    Scopul nostru este să oferim oamenilor{' '}
                                    <strong className="text-white border-b-2 border-indigo-400 pb-0.5">
                                        acces la instrumente psihologice reale
                                    </strong>{' '}
                                    pentru a se înțelege mai bine pe sine.
                                </p>

                                <p className="text-lg text-slate-200 leading-relaxed">
                                    Affinity Club îmbină{' '}
                                    <strong className="text-white border-b-2 border-indigo-400 pb-0.5">
                                        psihologia modernă cu tehnologia
                                    </strong>{' '}
                                    pentru a crea o experiență în care{' '}
                                    <strong className="text-indigo-400">
                                        profunzimea descoperirilor este mai importantă decât numărul de întrebări
                                    </strong>.
                                </p>

                                <div className="bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-emerald-500/20 rounded-2xl p-7 border border-white/10 mt-8">
                                    <div className="flex items-start gap-4">
                                        <Quote className="text-indigo-400 flex-shrink-0 mt-1" size={24} />
                                        <p className="text-slate-200 leading-relaxed">
                                            Fiecare utilizator parcurge un{' '}
                                            <strong className="text-white">proces unic de testare, structurat pe cinci niveluri</strong>,
                                            care acoperă valorile personale, stilul de atașament, maturitatea emoțională,
                                            tiparele inconștiente și profilul de personalitate – astfel încât rezultatele să fie{' '}
                                            <strong className="text-indigo-400">autentice și relevante</strong> pentru dezvoltarea personală.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ce ne diferențiază */}
            <section id="diferente" className="py-28 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-5 py-2.5 rounded-full text-sm font-semibold mb-6">
                            <Sparkles size={18} />
                            De ce suntem diferiți
                        </div>
                        <h2 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
                            Ce ne{' '}
                            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                diferențiază
                            </span>
                        </h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto font-light">
                            Nu doar teste – ci o experiență completă de autocunoaștere ghidată de psihologi
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Award,
                                gradient: "from-blue-600 to-indigo-600",
                                bgColor: "bg-blue-50",
                                borderColor: "border-blue-200",
                                title: "Teste validate clinic",
                                desc: "Teste psihologice reale, validate clinic, nu quizuri generice. Fiecare întrebare are un scop științific precis."
                            },
                            {
                                icon: TrendingUp,
                                gradient: "from-purple-600 to-pink-600",
                                bgColor: "bg-purple-50",
                                borderColor: "border-purple-200",
                                title: "4 niveluri progresive",
                                desc: "Profilare pe cinci niveluri, de la valorile conștiente până la tiparele inconștiente. Explorare completă și profundă."
                            },
                            {
                                icon: UserCheck,
                                gradient: "from-emerald-600 to-teal-600",
                                bgColor: "bg-emerald-50",
                                borderColor: "border-emerald-200",
                                title: "Create de psihologi",
                                desc: "Teste create și interpretate de psihologi cu experiență clinică. Nu algoritmi generici, ci expertiză reală."
                            },
                            {
                                icon: Brain,
                                gradient: "from-rose-600 to-orange-600",
                                bgColor: "bg-rose-50",
                                borderColor: "border-rose-200",
                                title: "Analize detaliate",
                                desc: "Acces la analize detaliate care explică fiecare dimensiune a personalității tale relaționale și cum te influențează."
                            },
                            {
                                icon: Users,
                                gradient: "from-indigo-600 to-blue-600",
                                bgColor: "bg-indigo-50",
                                borderColor: "border-indigo-200",
                                title: "Comunitate matură",
                                desc: "O comunitate matură, orientată spre autocunoaștere și dezvoltare personală autentică, nu dating superficial."
                            },
                            {
                                icon: Lightbulb,
                                gradient: "from-amber-600 to-yellow-600",
                                bgColor: "bg-amber-50",
                                borderColor: "border-amber-200",
                                title: "Insight-uri profunde",
                                desc: "Descoperiri care te schimbă cu adevărat – nu doar scoruri, ci înțelegere profundă despre cine ești în relații."
                            }
                        ].map((feature, idx) => (
                            <div
                                key={idx}
                                className={`group ${feature.bgColor} rounded-3xl p-8 border-2 ${feature.borderColor} hover:shadow-2xl hover:-translate-y-2 transition-all duration-500`}
                            >
                                <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                                    <feature.icon className="text-white" size={26} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-slate-600 leading-relaxed">
                                    {feature.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Procesul de testare */}
            <section className="py-28 px-6 bg-gradient-to-b from-white via-slate-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 px-5 py-2.5 rounded-full text-sm font-semibold text-indigo-700 mb-6 shadow-sm">
                            <Sparkles size={18} />
                            Procesul de evaluare
                        </div>
                        <h2 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
                            Cum funcționează{' '}
                            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                evaluarea
                            </span>
                        </h2>
                        <p className="text-xl text-slate-600 max-w-2xl mx-auto font-light leading-relaxed">
                            Un proces structurat, validat științific, cu rezultate personalizate
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {[
                            {
                                step: "01",
                                title: "Răspunzi sincer la teste",
                                desc: "Parcurgi cele 4 niveluri în ritmul tău. Progresul se salvează automat – poți reveni oricând.",
                                gradient: "from-indigo-500 to-blue-600",
                                bgColor: "bg-indigo-50",
                                borderColor: "border-indigo-200"
                            },
                            {
                                step: "02",
                                title: "Primești analiza Affinity",
                                desc: "După fiecare nivel, sistemul generează o analiză detaliată cu interpretări profesionale și recomandări personalizate.",
                                gradient: "from-purple-500 to-pink-600",
                                bgColor: "bg-purple-50",
                                borderColor: "border-purple-200"
                            },
                            {
                                step: "03",
                                title: "Explorezi profilul complet",
                                desc: "Toate dimensiunile personalității tale sunt prezentate într-o singură pagină de profil, ușor de înțeles și navigat.",
                                gradient: "from-emerald-500 to-teal-600",
                                bgColor: "bg-emerald-50",
                                borderColor: "border-emerald-200"
                            },
                            {
                                step: "04",
                                title: "Folosești insights pentru creștere",
                                desc: "Aplici ceea ce ai descoperit în relațiile tale – conștientizarea este primul pas către schimbare.",
                                gradient: "from-rose-500 to-orange-600",
                                bgColor: "bg-rose-50",
                                borderColor: "border-rose-200"
                            }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`group relative ${item.bgColor} rounded-3xl p-8 border-2 ${item.borderColor} hover:shadow-2xl hover:-translate-y-2 transition-all duration-500`}
                            >
                                <div className="relative flex items-start gap-6">
                                    <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                                        {item.step}
                                    </div>

                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-indigo-900 transition-colors">
                                            {item.title}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed text-base">
                                            {item.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mobile App Teaser */}
            <section className="py-28 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-12 text-white overflow-hidden">
                        <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>

                        <div className="relative z-10 max-w-3xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-full text-sm font-medium mb-8 border border-white/20">
                                <Smartphone size={18} />
                                <span>În curând disponibil</span>
                            </div>

                            <h2 className="text-4xl md:text-5xl font-bold mb-7 tracking-tight">
                                Aplicația Affinity Club
                            </h2>

                            <p className="text-xl mb-10 opacity-90 font-light leading-relaxed">
                                Versiuni dedicate pentru <strong className="font-semibold">iOS și Android</strong> – profil,
                                istoricul testelor și recomandări de dezvoltare personală într-un singur loc.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                {['App Store', 'Google Play'].map((store, idx) => (
                                    <div
                                        key={idx}
                                        className="group bg-white/10 backdrop-blur-md px-8 py-4 rounded-2xl border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer"
                                    >
                                        <div className="text-sm opacity-75 mb-1">Disponibil pe</div>
                                        <div className="text-lg font-bold flex items-center justify-center gap-2">
                                            {store}
                                            <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Final */}
            <section className="py-28 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white relative overflow-hidden">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="relative max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight leading-tight">
                        Ești gata să te cunoști cu adevărat?
                    </h2>
                    <p className="text-xl md:text-2xl mb-10 opacity-95 font-light max-w-2xl mx-auto">
                        Alătură-te comunității Affinity Club și descoperă-ți profilul psihologic complet,
                        ghidat de psihologi.
                    </p>

                    <button
                        onClick={() => navigate('/teste')}
                        className="group bg-white text-slate-900 px-14 py-6 rounded-full font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-2xl hover:shadow-white/25 inline-flex items-center gap-4 hover:gap-6"
                    >
                        Începe testele psihologice
                        <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
                        <div className="flex items-center gap-2">
                            <Shield size={18} className="text-emerald-400" />
                            <span>Teste validate clinic</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Zap size={18} className="text-emerald-400" />
                            <span>Analize personalizate</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Heart size={18} className="text-emerald-400" />
                            <span>100% confidențial</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;